'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CompoundInterestCalculator() {
  const t = useTranslations("tools.compoundInterestCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    principal: 10000,
    rate: 6,
    years: 10,
    compound: 12,
    contribution: 200,
    contributionFreq: 12,
  });

  const [results, setResults] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { principal, rate, years, compound, contribution, contributionFreq } = inputs;
    const rateDecimal = rate / 100;

    let finalAmount: number;
    let effectiveRate: number;

    if (compound === 0) { // Continuous
      const totalContributions = contribution * contributionFreq * years;
      finalAmount = principal * Math.exp(rateDecimal * years);

      if (contribution > 0) {
        for (let i = 0; i < years; i++) {
          const yearlyContribution = contribution * contributionFreq;
          const timeRemaining = years - i - 0.5;
          finalAmount += yearlyContribution * Math.exp(rateDecimal * timeRemaining);
        }
      }

      effectiveRate = (Math.exp(rateDecimal) - 1) * 100;
    } else {
      const n = compound;
      const r = rateDecimal / n;
      const nt = n * years;

      finalAmount = principal * Math.pow(1 + r, nt);

      if (contribution > 0) {
        const contributionsPerCompound = contributionFreq / n;
        const totalPeriods = nt;

        for (let i = 0; i < totalPeriods; i++) {
          const contributionThisPeriod = contribution * contributionsPerCompound;
          const periodsRemaining = totalPeriods - i;
          finalAmount += contributionThisPeriod * Math.pow(1 + r, periodsRemaining);
        }
      }

      effectiveRate = (Math.pow(1 + r, n) - 1) * 100;
    }

    const totalPrincipal = principal + (contribution * contributionFreq * years);
    const totalInterest = finalAmount - totalPrincipal;

    // Generate yearly breakdown
    const yearlyData = [];
    let balance = principal;
    const n = compound === 0 ? 365 : compound;
    const r = rateDecimal / n;

    for (let year = 1; year <= Math.min(years, 10); year++) {
      const startBalance = balance;
      const yearlyContribution = contribution * contributionFreq;

      if (compound === 0) {
        balance = balance * Math.exp(rateDecimal) + yearlyContribution * Math.exp(rateDecimal / 2);
      } else {
        const periodsInYear = n;
        for (let period = 0; period < periodsInYear; period++) {
          balance = balance * (1 + r);
          balance += (contribution * contributionFreq) / periodsInYear;
        }
      }

      const interestEarned = balance - startBalance - yearlyContribution;

      yearlyData.push({
        year,
        start: startBalance,
        contributions: yearlyContribution,
        interest: interestEarned,
        end: balance,
      });
    }

    setResults({
      finalAmount,
      totalPrincipal,
      totalInterest,
      effectiveRate,
      yearlyData,
    });
    setShowResults(true);
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '1.25rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const selectStyle = { ...inputStyle };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.15rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #2563eb',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsHeaderStyle = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem' },
    { gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.5rem', fontWeight: 500 },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { ...resultValueStyle, color: '#7c3aed' },
    { fontSize: '1.75rem' },
    { fontSize: '1.875rem' },
    { fontSize: '2rem' }
  );

  const chartContainerStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const chartBarsStyle = {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: '200px',
    marginTop: '1rem',
  };

  const chartBarStyle = {
    flex: 1,
    margin: '0 0.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
  };

  const barStyle = (height: number, isInterest: boolean) => ({
    width: '100%',
    height: `${height}%`,
    background: isInterest
      ? 'linear-gradient(to top, #f59e0b, #f97316)'
      : 'linear-gradient(to top, #2563eb, #7c3aed)',
    borderRadius: '0.5rem 0.5rem 0 0',
    transition: 'all 0.3s ease',
  });

  const barLabelStyle = getResponsiveStyle(
    { marginTop: '0.5rem', color: '#64748b', fontWeight: 600, textAlign: 'center' as const },
    { fontSize: '0.75rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' }
  );

  const breakdownTableStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'white',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    },
    {},
    {},
    {}
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
  };

  const thStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'left' as const,
      fontWeight: 600,
    },
    { padding: '0.75rem', fontSize: '0.85rem' },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #e2e8f0' },
    { padding: '0.75rem', fontSize: '0.85rem' },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem', marginBottom: '0.875rem' },
    { fontSize: '1.5rem', marginBottom: '1rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', marginBottom: '1rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = { marginBottom: '0.75rem', lineHeight: 1.6 };

  const insightCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
      borderLeft: '4px solid #2563eb',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const referenceStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.5 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderTitleStyle}>{t("toolTitle")}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="principal" style={labelStyle}>{t("principalLabel")}</label>
                <input
                  type="number"
                  id="principal"
                  step="0.01"
                  min="0"
                  value={inputs.principal}
                  onChange={(e) => setInputs({ ...inputs, principal: parseFloat(e.target.value) || 0 })}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="rate" style={labelStyle}>{t("rateLabel")}</label>
                <input
                  type="number"
                  id="rate"
                  step="0.01"
                  min="0"
                  max="100"
                  value={inputs.rate}
                  onChange={(e) => setInputs({ ...inputs, rate: parseFloat(e.target.value) || 0 })}
                  style={inputStyle}
                  required
                />
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="years" style={labelStyle}>{t("yearsLabel")}</label>
                <input
                  type="number"
                  id="years"
                  step="0.1"
                  min="0"
                  value={inputs.years}
                  onChange={(e) => setInputs({ ...inputs, years: parseFloat(e.target.value) || 0 })}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="compound" style={labelStyle}>{t("compoundLabel")}</label>
                <select
                  id="compound"
                  value={inputs.compound}
                  onChange={(e) => setInputs({ ...inputs, compound: parseFloat(e.target.value) })}
                  style={selectStyle}
                  required
                >
                  <option value="1">{t("compoundAnnually")}</option>
                  <option value="2">{t("compoundSemiannually")}</option>
                  <option value="4">{t("compoundQuarterly")}</option>
                  <option value="12">{t("compoundMonthly")}</option>
                  <option value="24">{t("compoundSemimonthly")}</option>
                  <option value="26">{t("compoundBiweekly")}</option>
                  <option value="52">{t("compoundWeekly")}</option>
                  <option value="365">{t("compoundDaily")}</option>
                  <option value="0">{t("compoundContinuously")}</option>
                </select>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="contribution" style={labelStyle}>{t("contributionLabel")}</label>
                <input
                  type="number"
                  id="contribution"
                  step="0.01"
                  min="0"
                  value={inputs.contribution}
                  onChange={(e) => setInputs({ ...inputs, contribution: parseFloat(e.target.value) || 0 })}
                  style={inputStyle}
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="contributionFreq" style={labelStyle}>{t("contributionFreqLabel")}</label>
                <select
                  id="contributionFreq"
                  value={inputs.contributionFreq}
                  onChange={(e) => setInputs({ ...inputs, contributionFreq: parseFloat(e.target.value) })}
                  style={selectStyle}
                >
                  <option value="12">{t("contributionMonthly")}</option>
                  <option value="4">{t("contributionQuarterly")}</option>
                  <option value="1">{t("contributionAnnually")}</option>
                </select>
              </div>
            </div>

            <button type="submit" style={calculateButtonStyle}>
              {t("calculateButton")}
            </button>
          </form>

          {showResults && results && (
            <div style={resultsStyle}>
              <h3 style={resultsHeaderStyle}>{t("resultsTitle")}</h3>

              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("finalAmountLabel")}</span>
                  <span style={resultValueHighlightStyle}>{formatCurrency(results.finalAmount)}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("totalPrincipalLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalPrincipal)}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("totalInterestLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalInterest)}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("effectiveRateLabel")}</span>
                  <span style={resultValueStyle}>{results.effectiveRate.toFixed(4)}%</span>
                </div>
              </div>

              <div style={chartContainerStyle}>
                <h4 style={{ margin: '0 0 1rem 0', color: colors.textPrimary }}>{t("growthBreakdownTitle")}</h4>
                <div style={chartBarsStyle}>
                  <div style={chartBarStyle}>
                    <div style={barStyle((results.totalPrincipal / results.finalAmount) * 100, false)}></div>
                    <div style={barLabelStyle}>
                      {t("principalLabel")}<br />{formatCurrency(results.totalPrincipal)}
                    </div>
                  </div>
                  <div style={chartBarStyle}>
                    <div style={barStyle((results.totalInterest / results.finalAmount) * 100, true)}></div>
                    <div style={barLabelStyle}>
                      {t("interestLabel")}<br />{formatCurrency(results.totalInterest)}
                    </div>
                  </div>
                </div>
              </div>

              <div style={breakdownTableStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("tableYear")}</th>
                      <th style={thStyle}>{t("tableStartBalance")}</th>
                      <th style={thStyle}>{t("tableContributions")}</th>
                      <th style={thStyle}>{t("tableInterestEarned")}</th>
                      <th style={thStyle}>{t("tableEndBalance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.yearlyData.map((data: any, index: number) => (
                      <tr key={index} style={{ background: index % 2 === 1 ? '#f8fafc' : 'white' }}>
                        <td style={tdStyle}><strong>{t("yearPrefix")} {data.year}</strong></td>
                        <td style={tdStyle}>{formatCurrency(data.start)}</td>
                        <td style={tdStyle}>{formatCurrency(data.contributions)}</td>
                        <td style={tdStyle}>{formatCurrency(data.interest)}</td>
                        <td style={tdStyle}><strong>{formatCurrency(data.end)}</strong></td>
                      </tr>
                    ))}
                    {inputs.years > 10 && (
                      <tr>
                        <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
                          {t("tableNote", { total: inputs.years })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToUseStep1")}</li>
          <li style={listItemStyle}>{t("howToUseStep2")}</li>
          <li style={listItemStyle}>{t("howToUseStep3")}</li>
          <li style={listItemStyle}>{t("howToUseStep4")}</li>
          <li style={listItemStyle}>{t("howToUseStep5")}</li>
          <li style={listItemStyle}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      {/* Latest Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>

        <div style={insightCardStyle}>
          <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
          <p style={paragraphStyle}>{t("insightsContent1")}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={h3Style}>{t("insightsSubtitle2")}</h3>
          <ul style={listStyle}>
            <li style={listItemStyle}>{t("insightsBullet1")}</li>
            <li style={listItemStyle}>{t("insightsBullet2")}</li>
            <li style={listItemStyle}>{t("insightsBullet3")}</li>
            <li style={listItemStyle}>{t("insightsBullet4")}</li>
            <li style={listItemStyle}>{t("insightsBullet5")}</li>
          </ul>
        </div>
      </section>

      {/* Details Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsContent1")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsContent2")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailsBullet1")}</li>
          <li style={listItemStyle}>{t("detailsBullet2")}</li>
          <li style={listItemStyle}>{t("detailsBullet3")}</li>
          <li style={listItemStyle}>{t("detailsBullet4")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsContent3")}</p>
      </section>

      {/* FAQ Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={referenceStyle}>
            <a href="https://sparkco.ai/blog/mastering-compound-interest-calculators-in-2025" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference1")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.bankrate.com/banking/savings/compound-savings-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference2")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.financialmentor.com/calculator/compound-interest-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference3")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.investor.gov/financial-tools-calculators/calculators/compound-interest-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference4")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.schwabmoneywise.com/compound-savings-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference5")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://compoundinterestcalculator.io" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference6")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}